import React from 'react';

import type { MenuSection } from 'src/gql/graphql';
import usePaths from 'src/hooks/use-paths';
import useUI from 'src/hooks/use-ui';
import { ProductCarousel as ProductCarouselV2 } from 'src/components/carousel-v2/types/product-carousel';
import { HighlightCard } from 'src/components/product-card-v2/highlight-card';
import { useReportCarouselRenderState } from 'src/components/carousels/carousel-render-state';
import { LockIconSimple } from 'src/assets/lock-icon-simple';
import { useCollectionCarousel } from './use-collection-carousel';

export type CollectionCarouselProps = {
  section?: MenuSection;
  sectionIndex?: number;
  showLink?: boolean;
  showTitle?: boolean;
};

export function CollectionCarousel(props: CollectionCarouselProps): JSX.Element | null {
  const { section, showLink, showTitle } = props;
  const { collectionId, label: sectionLabel, sortBy } = section ?? {};
  const { isEmbeddedCarousel } = useUI();

  const carouselProps = useCollectionCarousel({
    collectionId: collectionId ?? '',
    sortBy: sortBy ?? '',
  });

  const { products, loading, cardBadgeColor, collectionDisplayMode, slug } = carouselProps;
  const collectionHidden = collectionDisplayMode === 'hidden';
  useReportCarouselRenderState({
    loading: !collectionHidden && loading,
    hasContent: !collectionHidden && products.length > 0,
  });
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const { href: collectionHref } = usePaths({ collections: slug || collectionId || '' });

  // Don't render if exclusive collection is hidden (no access or feature flag disabled)
  if (collectionHidden) {
    return null;
  }

  if (!loading && !products.length) {
    return null;
  }
  const exclusiveCollectionCard = collectionDisplayMode === 'exclusive' && (
    <HighlightCard
      title='Exclusive Collection'
      icon={<LockIconSimple color='currentColor' />}
      href={collectionHref}
      isEmbeddedCarousel={isEmbeddedCarousel}
      message={
        <>
          You're in! <br />
          Check out select products inside.
        </>
      }
      customColor={cardBadgeColor}
      testId='exclusive-highlight-card'
    />
  );

  return (
    <ProductCarouselV2
      beforeProducts={exclusiveCollectionCard}
      products={products}
      isLoading={loading}
      title={sectionLabel}
      href={collectionHref}
      hideTitle={showTitle === false}
      hideLink={showLink === false}
    />
  );
}
